import { useMemo, useRef, useState } from "react";
import { LayoutGrid, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { OptionsDialog } from "@/components/OptionsDialog";
import { MPage, Footer, SearchBox, Loading, blueBtn } from "@/components/mobile/parts";
import { CategoryGrid } from "@/components/mobile/CategoryGrid";
import { ProductList } from "@/components/mobile/ProductList";
import { brl, fmtQty } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { norm, pad2 } from "@/lib/mobile";
import { cn } from "@/lib/utils";
import { isCode, resolveCode, scaleCfg, useScanner } from "@/lib/barcode";

export function AddItems({ products, categories, ready, cart, add, dec, total, count, onCart }) {
  const [cat, setCat] = useState(null);
  const [qc, setQc] = useState("");
  const [qp, setQp] = useState("");
  const [picking, setPicking] = useState(null);
  const cats = useMemo(() => [{ id: "all", name: "Todos" }, ...categories].filter((x) => x.id === "all" || norm(x.name).includes(norm(qc))), [categories, qc]);
  const list = useMemo(() => products.filter((p) => (!cat || cat.id === "all" || p.category_id === cat.id)
    && (!norm(qp) || norm(p.name).includes(norm(qp)) || (p.barcode || "").includes(qp.trim()))), [products, cat, qp]);
  const { session } = useAuth();
  const pick = (p, qty) => {
    if (p.option_groups?.length) return setPicking(p);
    add({ product: p, qty });
    return toast.success(`+${qty ? `${fmtQty(qty)} ${p.unit || "kg"}` : 1} ${p.name}`, { duration: 900 });
  };
  const box = useRef(null);
  const byCode = (c, fallback) => {
    const hit = resolveCode(products, c, scaleCfg(session.tenant.settings));
    const p = hit?.product || fallback;
    if (p) { pick(p, hit?.product ? hit.qty : undefined); setQp(""); }
    else if (hit) toast.error(`Etiqueta de balança: nenhum produto com o código de balança ${hit.missing}`);
    else if (isCode(c)) toast.error(`Nenhum produto com o código ${c}`);
  };
  useScanner((c) => byCode(c, null), box);
  const showProducts = !!cat || !!qp.trim();
  return (
    <>
      <div ref={box} className="space-y-2 px-4 pt-4">
        <SearchBox value={qc} onChange={(v) => { setQc(v); setCat(null); }} placeholder="Buscar categoria" icon={LayoutGrid} testId="m-search-category" />
        <SearchBox value={qp} onChange={setQp} onEnter={() => byCode(qp.trim(), list.length === 1 ? list[0] : null)} placeholder="Buscar produto ou código de barras" testId="m-search-product" />
      </div>
      <MPage>
        {!ready ? <Loading /> : showProducts
          ? <ProductList products={list} cart={cart} onPick={pick} onDec={dec} title={cat?.name || "Busca"} onBack={cat ? () => setCat(null) : null} />
          : <CategoryGrid categories={cats} products={products} onPick={setCat} />}
      </MPage>
      <Footer className="border-t border-slate-200 bg-white px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)]" testId="m-cart-footer">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-slate-500">Total do Pedido</p>
            <p className="font-display text-xl font-bold" data-testid="m-cart-total">{brl(total)}</p>
          </div>
          <Button className={cn("h-12 rounded-xl px-5 text-base", blueBtn)} onClick={onCart} data-testid="m-view-cart-btn">
            <ShoppingCart className="h-5 w-5" /><span>Ver Carrinho (<span data-testid="m-cart-count">{pad2(count)}</span>)</span>
          </Button>
        </div>
      </Footer>
      <OptionsDialog product={picking} onClose={() => setPicking(null)} onAdd={add} />
    </>
  );
}
